import React, { useMemo, useState, useEffect } from 'react';
import { MenuItem, ListItemIcon, ListItemText, Box, Card, Checkbox, Typography, FormControl, InputLabel, Select, MenuItem as MuiMenuItem, Button, Grid, TextField } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { tokens } from '../../theme';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { useGetSkillPosteRequirementsQuery, useGetSkillsQuery, useCreateSkillPosteRequirementMutation, useGetRequirementsByPosteQuery, useAssignRequirementsToPosteMutation } from '../../services/SkillsManagement';
import SkillRequirementList from './SkillRequirementList';
import { useDispatch, useSelector } from 'react-redux';
import { CLEAR_REQUIREMENTS_LIST, INITIALIZE_REQUIREMENTS } from '../../Reducers/Actions';

// Right-side form to add a requirement (separate component, exported)
export const AddRequirementForm = React.forwardRef(({ poste, onAdded, existingRequirements = [], showHeader = true, showFooter = true, onSelectedChange }, ref) => {
  const posteId = poste?.posteId ?? poste?.id;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { data: skillsResponse, error: skillsError, isLoading: loadingSkills, isFetching: isFetchingSkills } = useGetSkillsQuery(
    { limit: '100', getAllWithoutPagination: true },
    { refetchOnMountOrArgChange: true }
  );

  
  
  const skills = React.useMemo(() => {
    return skillsResponse?.data?.skills || skillsResponse?.skills || skillsResponse?.data?.rows || skillsResponse?.rows || [];
  }, [skillsResponse]);

  const [assignRequirementsToPoste, { isLoading }] = useAssignRequirementsToPosteMutation();
  const [createRequirement] = useCreateSkillPosteRequirementMutation();

  // Already required skills for this poste
  const requiredSet = React.useMemo(() => new Set((existingRequirements || []).map(r => Number(r.skill_id))), [existingRequirements]);

  // Selected skills with config
  const [selected, setSelected] = React.useState({}); // { [skillId]: { required_level, min_score, max_score } }

  const toggleSkill = (skillId, checked) => {
    setSelected((prev) => {
      const copy = { ...prev };
      if (checked) {
        copy[skillId] = copy[skillId] || { required_level: 1, min_score: 1, max_score: 3 };
      } else {
        delete copy[skillId];
      }
      const count = Object.keys(copy).length;
      if (typeof onSelectedChange === 'function') onSelectedChange(count);
      return copy;
    });
  };

  const updateField = (skillId, field, value) => {
    const id = Number(skillId);
    let n = Number(value);
    if (Number.isFinite(n)) {
      n = Math.min(10, Math.max(0, n));
    }
    setSelected(prev => ({ ...prev, [id]: { ...prev[id], [field]: n } }));
  };

  const toEntries = () => Object.entries(selected).filter(([skillId]) => !requiredSet.has(Number(skillId)));

  const levelToLabel = (lvl) => {
    const map = { 1: 'Awareness', 2: 'Skilled Application', 3: 'Management' };
    const n = Number(lvl);
    return map[n] || lvl;
  };

  const getItems = () => {
    const entries = toEntries();
    return entries.map(([skillId, cfg]) => ({
      skill_id: Number(skillId),
      required_level: levelToLabel(cfg.required_level),
      min_score: Number(cfg.min_score),
      max_score: Number(cfg.max_score),
    }));
  };

  const handleSave = async () => {
    if (!posteId) return;
    // filter out duplicates already required for this poste
    const entries = toEntries();
    if (entries.length === 0) return;

    // basic validation
    for (const [, cfg] of entries) {
      if (cfg.min_score > cfg.max_score) {
        console.error('Min score must be <= Max score');
        return;
      }
    }

    try {
      const items = getItems();
      if (items.length === 1) {
        // Use single create endpoint to mirror Swagger behavior
        const one = items[0];
        await createRequirement({ body: { poste_id: Number(posteId), ...one } }).unwrap();
      } else {
        // Try batch assign first; fallback to per-item create if server rejects
        try {
          await assignRequirementsToPoste({ posteId: Number(posteId), body: { items } }).unwrap();
        } catch (err) {
          // Fallback: create one by one
          await Promise.all(entries.map(([skillId, cfg]) =>
            createRequirement({
              body: {
                poste_id: Number(posteId),
                skill_id: Number(skillId),
                required_level: levelToLabel(cfg.required_level),
                min_score: Number(cfg.min_score),
                max_score: Number(cfg.max_score),
              }
            }).unwrap()
          ));
        }
      }
      if (typeof onAdded === 'function') onAdded();
      setSelected({});
    } catch (e) {
      console.error('Failed to save requirements:', e);
    }
  };

  React.useImperativeHandle(ref, () => ({
    save: handleSave,
    getSelectedCount: () => Object.keys(selected).length,
    getItems, // expose payload-ready items for poste creation flow
    getSelectedRaw: () => ({ ...selected }),
    isSaving: isLoading,
    canSave: Boolean(posteId) && Object.keys(selected).length > 0,
  }), [handleSave, selected, isLoading, posteId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
        <Grid container spacing={0.75} direction="column">
          {skills.map((s) => {
            const sid = s.id ?? s.skill_id;
            const isAlready = requiredSet.has(Number(sid));
            const isSelected = !!selected[sid];
            return (
              <Grid item size={{xs : 12}} key={sid}>
                <Card sx={{ width: '100%', p: 1, borderRadius: 1, opacity: isAlready ? 0.5 : 1, borderColor: isSelected ? 'primary.main' : 'divider', borderWidth: 1, borderStyle: 'solid' }}>
                  <Box sx={{ width: '100%', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Checkbox size="small" checked={isSelected} onChange={(e) => toggleSkill(sid, e.target.checked)} disabled={isAlready} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>{s.name}</Typography>
                      {s.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={isSelected ? { whiteSpace: 'normal' } : {
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {s.description}
                        </Typography>
                      )}
                      {isSelected && (
                        <>
                        <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                          <FormControl size="small">
                            <InputLabel id={`lvl-${sid}`}>Required Level</InputLabel>
                            <Select labelId={`lvl-${sid}`} label="Required Level" value={String(selected[sid]?.required_level ?? 1)} onChange={(e) => updateField(sid, 'required_level', e.target.value)}>
                              <MuiMenuItem value={1}>1 - Awareness</MuiMenuItem>
                              <MuiMenuItem value={2}>2 - Skilled Application</MuiMenuItem>
                              <MuiMenuItem value={3}>3 - Management</MuiMenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            type="number"
                            label="Min Score"
                            value={selected[sid]?.min_score ?? 1}
                            onChange={(e) => updateField(sid, 'min_score', Number(e.target.value))}
                            inputProps={{ min: 0, max: 10 }}
                          />
                          <TextField
                            size="small"
                            type="number"
                            label="Max Score"
                            value={selected[sid]?.max_score ?? 3}
                            onChange={(e) => updateField(sid, 'max_score', Number(e.target.value))}
                            inputProps={{ min: 0, max: 10 }}
                          />
                        </Box>
                        {(() => {
                          const lvl = Number(selected[sid]?.required_level ?? 1);
                          const desc = lvl === 1
                            ? (s.awareness_description || '')
                            : lvl === 2
                              ? (s.skilled_application_description || '')
                              : (s.management_description || '');
                          if (!desc) return null;
                          const lvlName = lvl === 1 ? 'Awareness' : lvl === 2 ? 'Skilled Application' : 'Management';
                          const bg = lvl === 1
                            ? alpha(colors.skyBlue[400], 0.1)
                            : lvl === 2
                              ? alpha(colors.Orange[400], 0.1)
                              : alpha(colors.redAccent[400], 0.1);
                          const border = lvl === 1
                            ? `1px solid ${alpha(colors.skyBlue[400], 0.2)}`
                            : lvl === 2
                              ? `1px solid ${alpha(colors.Orange[400], 0.2)}`
                              : `1px solid ${alpha(colors.redAccent[400], 0.2)}`;
                          return (
                            <Box sx={{ mt: 1, p: 1, borderRadius: 1, backgroundColor: bg, border }}>
                              <Typography variant="caption" sx={{ color: colors.grey[800] }}>
                                <strong>Level {lvl} - {lvlName}:</strong> {desc}
                              </Typography>
                            </Box>
                          );
                        })()}
                        </>
                      )}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      {showFooter && (
        <Box sx={{ position: 'sticky', bottom: 0, zIndex: 1, bgcolor: 'background.paper', pt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" disabled={isLoading || !posteId || Object.keys(selected).length === 0} onClick={handleSave}>
            Save Skills ({Object.keys(selected).length})
          </Button>
        </Box>
      )}
    </Box>
  );
}) 
;

// This action component opens the full-screen dialog to manage requirements for a poste
const AddRequirementsSkills = ({ row }) => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const poste = row?.original || row;
  const posteId = poste?.posteId ?? poste?.id;

  const { data: reqData, refetch: refetchReqs } = useGetRequirementsByPosteQuery({
    posteId: Number(posteId),
    pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 1000 }),
    sorting: '',
  }, { skip: !posteId, refetchOnMountOrArgChange: true });

  const requirementsFromApi = useMemo(() => {
    const items = reqData?.requirements || reqData?.data?.requirements || [];
    return items.map(r => ({
      id: r.skill_poste_id ?? r.id,
      skill_id: r.skill_id,
      required_level: r.required_level,
      min_score: r.min_score,
      max_score: r.max_score,
      // Do not enrich here to avoid extra skills fetching in this file
      skill: r.skill || null,
    }));
  }, [reqData]);

  // Select live requirements from Redux for rendering
  const requirements = useSelector((state) => state.requirements);

  // Sync to Redux requirements slice
  useEffect(() => {
    dispatch({ type: CLEAR_REQUIREMENTS_LIST });
    if (requirementsFromApi && requirementsFromApi.length > 0) {
      dispatch({ type: INITIALIZE_REQUIREMENTS, payload: requirementsFromApi });
    }
  }, [dispatch, requirementsFromApi]);

  const handleOpen = () => {
    setOpen(true);
    if (posteId) {
      // ensure fresh data for the selected poste
      setTimeout(() => refetchReqs(), 0);
    }
  };
  const handleClose = () => setOpen(false);

  return (
    <>
      <MenuItem onClick={handleOpen}>
        <ListItemIcon>
          <PlaylistAddCheckIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Requirement Skills</ListItemText>
      </MenuItem>

      <SkillRequirementList
        open={open}
        onClose={handleClose}
        poste={poste}
        requirements={requirements}
        rightFormProps={{ onAdded: () => refetchReqs() }}
      />
    </>
  );
};

export default AddRequirementsSkills;